import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Play, 
  Clock, 
  Trophy, 
  Lock,
  CheckCircle,
  Target,
  BookOpen,
  Award,
  TrendingUp,
  Users,
  Star
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { learningService } from '../services/learningService'
import { LearningModule, LearningVideo } from '../types/learning'

const ModuleDetailPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>()
  const [module, setModule] = useState<LearningModule | null>(null)
  const [videos, setVideos] = useState<LearningVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchModuleData = async () => {
      if (!moduleId) {
        setError('Module ID not provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const [moduleData, videosData] = await Promise.all([
          learningService.getModuleById(moduleId),
          learningService.getModuleVideos(moduleId)
        ])

        if (!moduleData) {
          setError('Module not found')
          return
        }

        setModule(moduleData)
        setVideos(videosData)
      } catch (err) {
        console.error('Error fetching module data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load module')
      } finally {
        setLoading(false)
      }
    }

    fetchModuleData()
  }, [moduleId])

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-500'
      case 'intermediate': return 'text-yellow-500'
      case 'advanced': return 'text-red-500'
      default: return 'text-background-500'
    }
  }

  const getDifficultyBg = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 border-green-500/30'
      case 'intermediate': return 'bg-yellow-500/20 border-yellow-500/30'
      case 'advanced': return 'bg-red-500/20 border-red-500/30'
      default: return 'bg-background-800 border-background-700'
    }
  }

  const calculateModuleProgress = () => {
    if (!videos.length) return { completed: 0, total: 0, percentage: 0 }
    
    const completed = videos.filter(video => video.progress?.completed).length
    const total = videos.length
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
    
    return { completed, total, percentage }
  }

  const calculateTotalXP = () => {
    return videos.reduce((total, video) => total + video.xp_reward, 0)
  }

  const calculateTotalDuration = () => {
    return videos.reduce((total, video) => total + (video.duration || 0), 0)
  }

  const getNextVideo = () => {
    return videos.find(video => !video.progress?.completed)
  }

  const isVideoUnlocked = (video: LearningVideo, index: number) => {
    // First video is always unlocked
    if (index === 0) return true
    
    // Check if previous video is completed
    const previousVideo = videos[index - 1]
    return previousVideo?.progress?.completed || false
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full mx-auto mb-6"
            />
            <p className="text-background-400">Loading module...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !module) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || 'Module not found'}</p>
            <Link 
              to="/learn" 
              className="luxury-button primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Learning
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const moduleProgress = calculateModuleProgress()
  const totalXP = calculateTotalXP()
  const totalDuration = calculateTotalDuration()
  const nextVideo = getNextVideo()

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="container mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/learn" 
            className="inline-flex items-center text-primary-500 hover:text-primary-400 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Learning Center
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-primary-500/20 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-3xl">{module.icon || '📚'}</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-background-100 mb-2">
                    {module.title}
                  </h1>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getDifficultyBg(module.difficulty)} ${getDifficultyColor(module.difficulty)}`}>
                    {module.difficulty}
                  </div>
                </div>
              </div>
              
              <p className="text-background-400 text-lg max-w-3xl mb-6">
                {module.description}
              </p>
            </div>
            
            {nextVideo && (
              <Link
                to={`/learn/video/${nextVideo.id}`}
                className="luxury-button primary"
              >
                <Play className="w-4 h-4 mr-2" />
                Continue Learning
              </Link>
            )}
          </div>
        </div>

        {/* Module Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary-500/20 rounded-lg mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-primary-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {moduleProgress.completed}/{moduleProgress.total}
            </div>
            <div className="text-sm text-background-500">
              Videos completed
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-lg mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {moduleProgress.percentage}%
            </div>
            <div className="text-sm text-background-500">
              Module progress
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-lg mx-auto mb-3">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {formatDuration(totalDuration)}
            </div>
            <div className="text-sm text-background-500">
              Total duration
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-lg mx-auto mb-3">
              <Trophy className="w-6 h-6 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {totalXP}
            </div>
            <div className="text-sm text-background-500">
              Total XP available
            </div>
          </div>
        </motion.div>

        {/* Progress Bar */}
        {moduleProgress.total > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="luxury-card p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-background-100">
                Module Progress
              </h3>
              <span className="text-sm text-background-500">
                {moduleProgress.completed} of {moduleProgress.total} videos completed
              </span>
            </div>
            
            <div className="w-full bg-background-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-primary-500 to-primary-400 h-3 rounded-full transition-all duration-500"
                style={{ width: `${moduleProgress.percentage}%` }}
              />
            </div>
          </motion.div>
        )}

        {/* Video List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-background-100 mb-6">
            Module Videos
          </h2>
          
          <div className="space-y-4">
            {videos.map((video, index) => {
              const isUnlocked = isVideoUnlocked(video, index)
              const isCompleted = video.progress?.completed
              const isNext = nextVideo?.id === video.id
              
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className={`luxury-card p-6 ${
                    isNext ? 'bg-primary-500/10 border-primary-500/20' : ''
                  } ${!isUnlocked ? 'opacity-60' : 'hover:scale-102 transition-transform'}`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Video Number & Status */}
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? 'bg-green-500/20 text-green-500' 
                          : isUnlocked 
                          ? 'bg-primary-500/20 text-primary-500'
                          : 'bg-background-700 text-background-500'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : !isUnlocked ? (
                          <Lock className="w-6 h-6" />
                        ) : (
                          <span className="font-bold">{index + 1}</span>
                        )}
                      </div>
                    </div>

                    {/* Video Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-background-100 mb-1">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-background-400 text-sm mb-2 line-clamp-2">
                              {video.description}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-sm text-background-500">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {formatDuration(video.duration)}
                            </div>
                            <div className="flex items-center">
                              <Trophy className="w-4 h-4 mr-1" />
                              {video.xp_reward} XP
                            </div>
                            <div className={`flex items-center ${getDifficultyColor(video.difficulty)}`}>
                              <Target className="w-4 h-4 mr-1" />
                              {video.difficulty}
                            </div>
                          </div>
                        </div>

                        {/* Action Button */}
                        <div className="ml-4">
                          {!isUnlocked ? (
                            <div className="text-sm text-background-500 bg-background-800 px-3 py-2 rounded-lg">
                              Complete previous video
                            </div>
                          ) : (
                            <Link
                              to={`/learn/video/${video.id}`}
                              className={`luxury-button ${isNext ? 'primary' : 'secondary'}`}
                            >
                              {isCompleted ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Review
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  {isNext ? 'Continue' : 'Watch'}
                                </>
                              )}
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {video.progress && !isCompleted && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-background-500 mb-1">
                            <span>Progress</span>
                            <span>{video.progress.completion_percentage}%</span>
                          </div>
                          <div className="w-full bg-background-700 rounded-full h-2">
                            <div 
                              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${video.progress.completion_percentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Completion Certificate */}
        {moduleProgress.completed === moduleProgress.total && moduleProgress.total > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="luxury-card p-8 mt-8 bg-gradient-to-r from-green-500/10 to-primary-500/10 border-green-500/20"
          >
            <div className="text-center">
              <div className="flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mx-auto mb-4">
                <Award className="w-10 h-10 text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-background-100 mb-2">
                Module Complete! 🎉
              </h3>
              
              <p className="text-background-400 mb-6">
                Congratulations! You've completed all videos in this module and earned {totalXP} XP.
              </p>
              
              <div className="flex items-center justify-center space-x-6">
                <Link
                  to="/learn"
                  className="luxury-button primary"
                >
                  Explore More Modules
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default ModuleDetailPage
